import React from 'react';
import moment from 'moment';

import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';
import { parseEventDates } from '../../util/eventDateHelpers';

import { Heading } from '../../components';

import css from './ListingPage.module.css';

/**
 * Helper: Check if a value is "empty" (not displayable)
 * Returns true if value should be hidden
 */
const isEmptyValue = (value) => {
  return (
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  );
};

const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl } = props;

  if (!publicData || !listingFieldConfigs) {
    return null;
  }

  const isLocalProducers = publicData?.categoryLevel1 === 'local-producers';

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const isTargetListingType = isLocalProducers || isFieldForListingType(publicData.listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && !isEmptyValue(value)) {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      // Special handling for eventDate field: format Unix timestamp as date
      if (key === 'eventDate' && (schemaType === 'long' || schemaType === 'text')) {
        // Handle both single timestamp (long) and array of timestamps (text/JSON)
        if (schemaType === 'text') {
          // Parse multiple dates from JSON array or comma-separated string
          const dates = parseEventDates(value);
          if (dates.length > 0) {
            const formattedDates = dates
              .map(timestamp => moment(timestamp).format('ddd, MMM D, YYYY'))
              .join('; ');
            return filteredConfigs.concat({ key, value: formattedDates, label });
          }
        } else {
          // Handle single date (long format - backward compat)
          const eventMoment = moment(value);
          const formattedDate = eventMoment.format('DD MMM YYYY');
          return filteredConfigs.concat({ key, value: formattedDate, label });
        }
      }

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label })
        : schemaType === 'boolean'
        ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
        : schemaType === 'long'
        ? filteredConfigs.concat({ key, value, label })
        : filteredConfigs;
    }
    return filteredConfigs;
  };

  const existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);

  return existingListingFields.length > 0 ? (
    <section className={css.sectionDetails}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </Heading>
      <ul className={css.details}>
        {existingListingFields.map(detail => (
          <li key={detail.key} className={css.detailsRow}>
            <span className={css.detailLabel}>{detail.label}</span>
            <span>{detail.value}</span>
          </li>
        ))}
      </ul>
    </section>
  ) : null;
};

export default SectionDetailsMaybe;
